/**
 * actionPanel.ts — the dynamic, priority-driven "actions needed" rail
 * (client-side half; server/helpers/actionPanel.js is the real-mode source).
 *
 * Canonical item shape + the ranking function BOTH demo and real paths run
 * through (useActionPanelItems, domain/hooks.ts) — this is the one place
 * severity-escalation and sort order are decided, so ranking behaviour is
 * identical regardless of where the items came from.
 *
 * CATEGORY_BASE_SEVERITY mirrors server/helpers/actionPanel.js's constant
 * of the same values exactly. It's a small config table, not logic, and
 * there's no shared-package boundary between this client bundle and the
 * server to import a single literal from — see that file's header comment.
 */

import type { WorkspaceSeed, Profile } from "./types";
import type { AttentionItem, AttentionSeverity, BriefingAlert, RailSeverity } from "@/design/components";
import { workspaceIdForProgrammeId } from "./seed/workspaces";

export type ActionSeverity = "serious" | "attention" | "info";
export type ActionCategory =
  | "gate"
  | "creation_request"
  | "edit_request"
  | "confirmation"
  | "disbursement"
  | "query_reply"
  | "evidence_due"
  | "approval";

export interface ActionItem {
  id: string;
  category: ActionCategory;
  title: string;
  detail?: string | null;
  /** Raw base-currency figure (disbursement items only) - lets a persona-specific
   *  view format it through its own money() rather than baking a fixed string in. */
  amount?: number;
  severity: ActionSeverity;
  href: string;
  createdAt: string;
  /** The project this item concerns, when it concerns exactly one (absent
   * for e.g. `gate`). Real numeric-string id server-side; demo mode leaves
   * this unset (see deriveActionPanelItems's header comment). */
  projectId?: string;
  /** The project's numeric DB programme id (not a workspace id — this app's
   * "workspace" concept only exists client-side, see seed/workspaces.ts).
   * toAttentionItems/toBriefingAlerts resolve this to a workspace id so a
   * click can switch into the right one regardless of what's currently
   * active, rather than assuming the item concerns the active workspace. */
  programmeId?: string;
}

const CATEGORY_BASE_SEVERITY: Record<ActionCategory, ActionSeverity> = {
  gate: "attention",
  creation_request: "info",
  edit_request: "info",
  confirmation: "info",
  disbursement: "attention",
  query_reply: "attention",
  evidence_due: "info",
  approval: "info",
};

// Role -> panel title, the "easy to extend to new roles" mechanism: adding
// a role later is one new entry here plus (only if it needs a genuinely new
// signal) one new category above - never a fourth copy of rail-wiring code.
export const ACTION_PANEL_TITLE: Record<string, string> = {
  oversight: "Needs You",
  funder: "Awaiting You",
  contractor: "Briefing",
};

function item(input: Omit<ActionItem, "severity"> & { severity?: ActionSeverity }): ActionItem {
  return { ...input, severity: input.severity ?? CATEGORY_BASE_SEVERITY[input.category] };
}

const SEVERITY_RANK: Record<ActionSeverity, number> = { serious: 3, attention: 2, info: 1 };
const NEXT_SEVERITY: Record<ActionSeverity, ActionSeverity> = {
  info: "attention",
  attention: "serious",
  serious: "serious",
};
const AGE_ESCALATION_HOURS = 48;

export function ageHoursOf(createdAt: string, now: number = Date.now()): number {
  const t = new Date(createdAt).getTime();
  return Number.isFinite(t) ? Math.max(0, (now - t) / 3_600_000) : 0;
}

/**
 * Two-factor priority score: base severity by category (above), escalated
 * one level once an item crosses AGE_ESCALATION_HOURS (capped at "serious"),
 * then sorted severity-first, oldest-first within a tier - matching the
 * "oldest first" convention listApproverQueue etc. already use server-side.
 */
export function rankActionItems(items: ActionItem[], now: number = Date.now()): ActionItem[] {
  return items
    .map((it) => {
      const hours = ageHoursOf(it.createdAt, now);
      const severity = hours >= AGE_ESCALATION_HOURS ? NEXT_SEVERITY[it.severity] : it.severity;
      return { item: { ...it, severity }, hours };
    })
    .sort((a, b) => {
      const rankDiff = SEVERITY_RANK[b.item.severity] - SEVERITY_RANK[a.item.severity];
      return rankDiff !== 0 ? rankDiff : b.hours - a.hours;
    })
    .map((s) => s.item);
}

/**
 * Demo-mode equivalent of server/helpers/actionPanel.js's three
 * aggregators, derived from the same seed fields the rest of the app's
 * demo mode already reads (deriveBriefingAlerts, FunderDashboardLayout,
 * Inbox.tsx's hasOpenQuery/readyToSpend). Covers the categories with a
 * clear, already-established demo-seed representation; creation_request/
 * edit_request have no established demo-seed shape to derive from and are
 * left out of the demo path specifically (real mode covers them) - stated,
 * not silently dropped.
 */
export function deriveActionPanelItems(seed: WorkspaceSeed, profile: Profile, actorId: string): ActionItem[] {
  const items: ActionItem[] = [];

  if (profile.group === "oversight") {
    for (const org of seed.organisations) {
      if (org.pipelineStage === "awaiting_gate") {
        items.push(
          item({
            id: `gate-${org.id}`,
            category: "gate",
            title: `${org.name} — awaiting approval gate`,
            href: "/stratus/approvals",
            createdAt: new Date().toISOString(),
          })
        );
      }
    }
    const myConfirmProjectIds = new Set(
      (seed.memberships ?? []).filter((m) => m.personId === actorId && m.role === "stratus_admin").map((m) => m.projectId)
    );
    for (const r of seed.spendRequests) {
      if (r.state === "evidence_submitted" && (myConfirmProjectIds.size === 0 || myConfirmProjectIds.has(r.projectId))) {
        items.push(
          item({
            id: `confirm-${r.id}`,
            category: "confirmation",
            title: `${r.title} — evidence ready to confirm`,
            href: "/stratus/approvals",
            createdAt: r.submittedAt,
          })
        );
      }
    }
  }

  if (profile.group === "funder") {
    for (const d of seed.disbursements) {
      if (d.state === "ready_for_funder") {
        const milestone = seed.milestones.find((m) => m.id === d.milestoneId);
        items.push(
          item({
            id: `disbursement-${d.id}`,
            category: "disbursement",
            title: milestone ? `${milestone.name} disbursement` : "Disbursement ready",
            amount: d.amount,
            href: "/funder/approvals",
            createdAt: d.routedAt || new Date().toISOString(),
          })
        );
      }
    }
  }

  if (profile.group === "contractor" || profile.group === "reporter") {
    const myAsks = seed.spendRequests.filter((r) => r.reporterId === actorId);
    for (const r of myAsks) {
      const lastThread = r.thread?.[r.thread.length - 1];
      if (r.state === "proposed" && lastThread?.kind === "query") {
        items.push(
          item({
            id: `query-${r.id}`,
            category: "query_reply",
            title: `${r.title} — reply needed`,
            href: "/reporter",
            createdAt: lastThread.at,
          })
        );
      } else if (r.state === "approved_for_spend") {
        items.push(
          item({
            id: `evidence-${r.id}`,
            category: "evidence_due",
            title: `${r.title} — evidence needed`,
            href: `/reporter/evidence/${r.id}`,
            createdAt: r.reviewedAt || r.submittedAt,
          })
        );
      }
    }

    const myLeadProjectIds = new Set(
      (seed.memberships ?? [])
        .filter((m) => m.personId === actorId && (m.role === "prime_lead" || m.role === "sub_lead"))
        .map((m) => m.projectId)
    );
    if (myLeadProjectIds.size > 0) {
      for (const r of seed.spendRequests) {
        if (r.state === "proposed" && r.projectId && myLeadProjectIds.has(r.projectId)) {
          items.push(
            item({
              id: `approve-${r.id}`,
              category: "approval",
              title: `${r.title} — awaiting your approval`,
              // A lead reviewing someone else's ask belongs on the real
              // Approvals surface, not the flat reporter inbox (the same
              // wrong-page bug real mode had — demo already knows its own
              // workspace here, so it's fixed at the source rather than via
              // toAttentionItems's programmeId-based correction).
              href: `/workspace/${seed.workspace.id}/approvals`,
              createdAt: r.submittedAt,
            })
          );
        }
      }
    }
  }

  return items;
}

// --- Rendering through the existing rail components -------------------
// No new UI primitive needed for this panel - Stratus/Funder already
// render their rail via AttentionList (AttentionSeverity vocabulary),
// Contractor via Briefing (BriefingAlert, which already shares this
// module's serious|attention|info vocabulary directly). These two mappers
// are the only "view layer" translation needed, shared so both dashboard
// layouts that use AttentionList map identically.

const SEVERITY_TO_ATTENTION: Record<ActionSeverity, AttentionSeverity> = {
  serious: "overdue",
  attention: "due_soon",
  info: "info",
};

/** Compact age string for AttentionItem's trailing `figure` slot (e.g. "3h", "2d"). */
export function shortAge(createdAt: string, now: number = Date.now()): string {
  const hours = ageHoursOf(createdAt, now);
  if (hours < 1) return "<1h";
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * `figure` defaults to age (the uniform "how stale is this" signal every
 * other rail item uses), but a caller can override it per item - e.g. the
 * funder rail shows the tranche amount for disbursement items instead,
 * since that's the one fact that page has always led with.
 */
export function toAttentionItems(
  items: ActionItem[],
  figureOf: (item: ActionItem) => string = (it) => shortAge(it.createdAt)
): AttentionItem[] {
  return items.map((it) => {
    const workspaceId = workspaceIdForProgrammeId(it.programmeId);
    return {
      id: it.id,
      severity: SEVERITY_TO_ATTENTION[it.severity],
      kind: it.category,
      body: it.title,
      figure: figureOf(it),
      href: hrefFor(it, workspaceId),
      workspaceId,
    };
  });
}

// A contractor lead's "approval" item is real-mode's only remaining wrong-page
// case (server can't build a workspace-scoped URL itself - see
// actionPanel.js's own comment on this href) - corrected here, the one place
// both the category and the resolved workspaceId are available together.
// No-ops for demo (already correct at the source) and for any item whose
// workspaceId didn't resolve (falls back to the server's own href, unchanged).
function hrefFor(it: ActionItem, workspaceId: string | undefined): string {
  if (it.category === "approval" && workspaceId) return `/workspace/${workspaceId}/approvals`;
  return it.href;
}

// Highest attention severity -> the rail's count-chip tint, so "Needs you · 3"
// reads at the right alarm level. Shared here (not per-layout) so Stratus and
// Funder don't each keep their own copy of the same rank table.
const ATTENTION_RANK: Record<AttentionSeverity, number> = { overdue: 3, due_soon: 2, watch: 1, info: 0 };
const ATTENTION_TO_RAIL: Record<AttentionSeverity, RailSeverity> = {
  overdue: "bad",
  due_soon: "warn",
  watch: "info",
  info: "neutral",
};

export function topAttentionSeverity(items: { severity?: AttentionSeverity }[]): RailSeverity {
  let top: AttentionSeverity = "info";
  for (const it of items) {
    const s = it.severity ?? "info";
    if (ATTENTION_RANK[s] > ATTENTION_RANK[top]) top = s;
  }
  return ATTENTION_TO_RAIL[top];
}

export function toBriefingAlerts(items: ActionItem[]): BriefingAlert[] {
  return items.map((it) => {
    const workspaceId = workspaceIdForProgrammeId(it.programmeId);
    return {
      id: it.id,
      severity: it.severity,
      title: it.title,
      detail: it.detail ?? undefined,
      action: { label: "View", href: hrefFor(it, workspaceId) },
      workspaceId,
    };
  });
}
